import {DoppeDtoAction, DoppeDtoActionViewType} from '../../client-server-common/types/doppe-dto-action';
import {DoppeViewerClientServices} from '../../client/doppe-user-app/doppe-viewer-client-services';
import {BymoPageViewerContextData, BymoPageViewerMode} from '../bymo-page-viewer-context/bymo-page-viewer-context';
import {BymoPageContextProps, IDoppeViewerActionContext} from '../../doppe-sdk';
import {
    DoppeActionWidgetOpeningMode,
    DoppeBiEmptyTargetUrl,
    DoppeDtoSpeedDialLinkViewerData
} from '../../client-server-common';
import {SessionStorageAccessor} from '@wix/devzai-utils-dom';
import {
    DoppePageViewerPreventedOperation
} from '../../client/bymo-page-preview-service/bymo-page-preview-host-service-api';
import {assertDefined, asyncDelay, evaluateFunction, Price} from '@wix/devzai-utils-common';
import {DoppeViewerModalsController} from '../doppe-viewer-modals-controller/doppe-viewer-modals-controller';
import React from 'react';
import {DoppeViewerActionPopup} from '../doppe-viewer-action-popup/doppe-viewer-action-popup';

export class DoppeViewerActionContext<PROPS extends {}> implements IDoppeViewerActionContext<PROPS> {

    private actionIndex;
    private actionTogglingController;
    private fullScreenModeTogglingController;
    private clientServices;
    private viewerContextData;
    private bymoPageContextProps;

    public readonly modalsController;

    public readonly action;

    constructor(
        options: {
            action: DoppeDtoAction<PROPS>;
            actionIndex: number,

            clientServices: DoppeViewerClientServices;
            actionTogglingController?: DoppeViewerActionContext.ActionTogglingController;
            fullScreenModeTogglingController?: DoppeViewerActionContext.FullScreenModeTogglingController;
            modalsController: DoppeViewerModalsController;
            viewerContextData: BymoPageViewerContextData;
            bymoPageContextProps: BymoPageContextProps;
        }
    ) {
        this.action = options.action;
        this.actionIndex = options.actionIndex;

        this.actionTogglingController = options.actionTogglingController;
        this.fullScreenModeTogglingController = options.fullScreenModeTogglingController;
        this.clientServices = options.clientServices;
        this.viewerContextData = options.viewerContextData;
        this.bymoPageContextProps = options.bymoPageContextProps;
        this.modalsController = options.modalsController;
    }

    public async __apiHealthCheck() {
        await this.clientServices.getApiClient().health();
    }

    public navigateToUrl(targetUrl: string, newTab = false) {
        const viewerContextData = this.viewerContextData;

        if (viewerContextData.mode === BymoPageViewerMode.LivePreview) {
            viewerContextData.bymoPagePreviewHostServiceApiClient.emitBymoPagePreviewEvent('eventLinkNavigationPrevented', {
                url: targetUrl
            })
        } else {
            if (newTab) {
                window.open(targetUrl, '_blank', 'noopener');
            } else {
                location.href = targetUrl;
            }
        }
    }

    public async performViewerOnlyCallback(actionCallback: () => Promise<void>) {
        const viewerContextData = this.viewerContextData;

        if (viewerContextData.mode === BymoPageViewerMode.LivePreview) {
            viewerContextData.bymoPagePreviewHostServiceApiClient.emitBymoPagePreviewEvent('eventActionPrevented', {});
        } else {
            await actionCallback();
        }
    }

    public async resolveSpeedDialLinkFromCode(linkCode: string) {
        // In the future, the speed dial link resolving might be asynchronous.
        // In this case, we have to make sure that in case of an error, the returned value would be undefined.
        // This will make the resolveSpeedDialLinkFromCode function safe to be called.
        try {
            return this.bymoPageContextProps.speedDialData ?
                this.bymoPageContextProps.speedDialData.links.find(
                    link => link.speedDialCode !== null && link.speedDialCode.toLowerCase() === linkCode.toLowerCase()
                ) :
                undefined
        } catch (error) {
            return undefined;
        }
    }

    public async navigateToSpeedDialLink(speedDialLink: DoppeDtoSpeedDialLinkViewerData, newTab: boolean) {

        const viewerContextData = this.viewerContextData;

        if (viewerContextData.mode === BymoPageViewerMode.Live) {

            this.clientServices.viewerAnalyticsClient.logEvent('uouSpeedDialLinkRedirected', {
                pageId: viewerContextData.bymoPageId,
                hypeUserId: viewerContextData.doppeUserId,
                linkId: speedDialLink.id,
                destinationUrl: speedDialLink.targetUrl,
            });

            SessionStorageAccessor.updateItem<number>(
                `speedDialRedirected: ${viewerContextData.doppeUserId} ${viewerContextData.bymoPageId} ${speedDialLink.id}`,
                () => Date.now()
            );

            // SessionStorageAccessor.setItem(DisableBackTrapSessionStorageKey, true);
        }

        this.navigateToUrl(speedDialLink.targetUrl, newTab);

        this.reportGoalAchieved();
    }

    public reportGoalAchieved(innerItemId?: string, innerItemPosition?: number) {
        const viewerContextData = this.viewerContextData;

        if (viewerContextData.mode === BymoPageViewerMode.Live) {
            this.clientServices.viewerAnalyticsClient.logEvent('uouActionClicked', {
                actionId: this.action.id,
                innerItemId: innerItemId,
                pageId: viewerContextData.bymoPageId,
                hypeUserId: viewerContextData.doppeUserId,
                linkId: viewerContextData.linkId,
                itemPosition: this.actionIndex,
                actionType: this.action.actionTypeId,
                destinationUrl: this.bymoPageContextProps.targetUrl ?? DoppeBiEmptyTargetUrl,
                pageType: viewerContextData.pageType,
            });

            if (innerItemId) {
                this.clientServices.viewerAnalyticsClient.logEvent('uouActionInnerItemGoalAchieved', {
                    actionId: this.action.id,
                    innerItemId: innerItemId,
                    innerItemPosition: innerItemPosition ?? 0,
                    pageId: viewerContextData.bymoPageId,
                    hypeUserId: viewerContextData.doppeUserId,
                    linkId: viewerContextData.linkId,
                    itemPosition: this.actionIndex,
                    actionType: this.action.actionTypeId,
                    destinationUrl: this.bymoPageContextProps.targetUrl ?? DoppeBiEmptyTargetUrl,
                    pageType: viewerContextData.pageType,
                });
            }

            this.clientServices.viewerAnalyticsClient.logEvent('uouAnyActionClicked', {
                pageId: viewerContextData.bymoPageId,
                hypeUserId: viewerContextData.doppeUserId,
                linkId: viewerContextData.linkId,
                destinationUrl: this.bymoPageContextProps.targetUrl ?? DoppeBiEmptyTargetUrl,
                pageType: viewerContextData.pageType,
            });

            this.clientServices.viewerAnalyticsClient.logEvent('uouEngagementClicked', {
                pageId: viewerContextData.bymoPageId,
                hypeUserId: viewerContextData.doppeUserId,
                linkId: viewerContextData.linkId,
                destinationUrl: this.bymoPageContextProps.targetUrl ?? DoppeBiEmptyTargetUrl,
                actionType: this.action.actionTypeId,
                actionName: this.action.name,
            });
        }
    }

    public reportInnerItemViewed(innerItemId: string, innerItemPosition?: number) {
        const viewerContextData = this.viewerContextData;

        if (viewerContextData.mode === BymoPageViewerMode.Live) {
            this.clientServices.viewerAnalyticsClient.logEvent('uouViewedInnerItemGoalAchieved', {
                actionId: this.action.id,
                innerItemId: innerItemId,
                innerItemPosition: innerItemPosition ?? 0,
                pageId: viewerContextData.bymoPageId,
                hypeUserId: viewerContextData.doppeUserId,
                linkId: viewerContextData.linkId,
                itemPosition: this.actionIndex,
                actionType: this.action.actionTypeId,
                destinationUrl: this.bymoPageContextProps.targetUrl ?? DoppeBiEmptyTargetUrl,
                pageType: viewerContextData.pageType,
            });
        }
    }


    public async addContact(
        contactInfo: {
            email?: string;
            phone?: string;
            contactName?: string;
            zipCode?: string;
            address?: string
            listId?: string;
        },
    ) {
        const viewerContextData = this.viewerContextData;

        if (viewerContextData.mode === BymoPageViewerMode.Live) {
            await this.clientServices.getApiClient().addContact({
                contactName: contactInfo.contactName,
                phone: contactInfo.phone,
                listId: contactInfo.listId,
                email: contactInfo.email,
                address: contactInfo.address,
                zipcode: contactInfo.zipCode,
                actionId: this.action.id,
                pageId: viewerContextData.bymoPageId,
                linkId: viewerContextData.linkId,
                targetUrl: this.bymoPageContextProps.targetUrl,
            });

        } else if (viewerContextData.mode === BymoPageViewerMode.LivePreview) {

            await asyncDelay(400);

            viewerContextData.bymoPagePreviewHostServiceApiClient.emitBymoPagePreviewEvent('eventActionPrevented', {
                preventedOperation: DoppePageViewerPreventedOperation.ContactFormSubmission
            });
        }
    };

    public async submitContactForm(
        contactInfo: {
            email?: string;
            phone?: string;
            contactName?: string;
            zipCode?: string;
            address?: string;
            listId?: string;
            message?: string;
        }
    ) {
        const viewerContextData = this.viewerContextData;

        if (viewerContextData.mode === BymoPageViewerMode.Live) {
            await this.clientServices.getApiClient().addContact({
                contactName: contactInfo.contactName,
                phone: contactInfo.phone,
                listId: contactInfo.listId,
                email: contactInfo.email,
                address: contactInfo.address,
                zipcode: contactInfo.zipCode,
                message: contactInfo.message,
                actionId: this.action.id,
                pageId: viewerContextData.bymoPageId,
                linkId: viewerContextData.linkId,
                targetUrl: this.bymoPageContextProps.targetUrl,
            });

            this.clientServices.viewerAnalyticsClient.logEvent('uouFormSubmitted', {
                actionId: this.action.id,
                pageId: viewerContextData.bymoPageId,
                hypeUserId: viewerContextData.doppeUserId,
                linkId: viewerContextData.linkId,
                destinationUrl: this.bymoPageContextProps.targetUrl ?? '',
            })

        } else if (viewerContextData.mode === BymoPageViewerMode.LivePreview) {

            await asyncDelay(400);

            viewerContextData.bymoPagePreviewHostServiceApiClient.emitBymoPagePreviewEvent('eventActionPrevented', {
                preventedOperation: DoppePageViewerPreventedOperation.ContactFormSubmission
            });
        }
    };

    public showNotification(
        options: {
            message: string;
        }
    ) {
        const notificationController = this.clientServices.uiService.defaultNotificationOpener.open({
            message: options.message,
            onClose: () => {
                notificationController.close();
            }
        })
    }

    public redirectToTargetUrl(
        options: {
            additionalMessage?: string;
            countdownSeconds?: number;
        } = {}
    ) {
        const additionalMessage = options.additionalMessage;
        const countdownSeconds = options.countdownSeconds ?? 3;

        const targetUrl = this.bymoPageContextProps.targetUrl;

        if (!targetUrl) {
            return;
        }

        const viewerContextData = this.viewerContextData;
        if (viewerContextData.mode === BymoPageViewerMode.Live) {

            this.clientServices.viewerAnalyticsClient.logEvent('uouAutoRedirectTriggered', {
                actionId: this.action.id,
                pageId: viewerContextData.bymoPageId,
                hypeUserId: viewerContextData.doppeUserId,
                linkId: viewerContextData.linkId,
                actionType: this.action.actionTypeId,
                contextMetaData: JSON.stringify({
                    actionId: this.action.id,
                    actionType: this.action.actionTypeId,
                }),
                destinationUrl: targetUrl,
            })
        }

        const notificationController = this.clientServices.uiService.autoRedirectNotificationOpener.open({
            targetUrl: targetUrl,
            additionalMessage: additionalMessage,
            countdownSeconds: countdownSeconds,
            onCountdownComplete: () => {

                const viewerContextData = this.viewerContextData;

                if (viewerContextData.mode === BymoPageViewerMode.Live) {

                    this.clientServices.viewerAnalyticsClient.logEvent('uouAutoRedirected', {
                        actionId: this.action.id,
                        pageId: viewerContextData.bymoPageId,
                        hypeUserId: viewerContextData.doppeUserId,
                        linkId: viewerContextData.linkId,
                        actionType: this.action.actionTypeId,
                        contextMetaData: JSON.stringify({
                            actionId: this.action.id,
                            actionType: this.action.actionTypeId,
                        }),
                        destinationUrl: targetUrl,
                    })

                    this.clientServices.bymoService.skipToTargetUrl({
                        targetUrl: targetUrl,
                        livePageData: viewerContextData
                    })
                } else if (viewerContextData.mode === BymoPageViewerMode.LiveInApp) {
                    location.href = targetUrl;
                } else if (viewerContextData.mode === BymoPageViewerMode.LivePreview) {
                    viewerContextData.bymoPagePreviewHostServiceApiClient.emitBymoPagePreviewEvent('eventAutoSkipPrevented', {})
                }

                notificationController.close();
            },
            onCancel: () => {
                const viewerContextData = this.viewerContextData;

                if (viewerContextData.mode === BymoPageViewerMode.Live) {

                    this.clientServices.viewerAnalyticsClient.logEvent('uouAutoRedirectCanceled', {
                        actionId: this.action.id,
                        pageId: viewerContextData.bymoPageId,
                        hypeUserId: viewerContextData.doppeUserId,
                        linkId: viewerContextData.linkId,
                        contextMetaData: JSON.stringify({
                            actionId: this.action.id,
                            actionType: this.action.actionTypeId,
                        }),
                        destinationUrl: targetUrl,
                    })
                } else if (viewerContextData.mode === BymoPageViewerMode.LivePreview) {
                    viewerContextData.bymoPagePreviewHostServiceApiClient.emitBymoPagePreviewEvent('eventAutoSkipPrevented', {})
                }

                notificationController.close();
            }
        })
    }

    public getShowActionHeader() {
        return this.action.showActionHeader !== undefined ? this.action.showActionHeader : true;
    }

    public getActionViewType() {
        return this.action.viewType ?? DoppeDtoActionViewType.ActionButton;
    }

    public canBeToggled() {
        return this.actionTogglingController !== undefined &&
            this.getActionViewType() === DoppeDtoActionViewType.ActionButton &&
            this.action.widgetOpeningMode !== DoppeActionWidgetOpeningMode.OpenCTA;
    }

    private getActionTogglingController() {
        return this.canBeToggled() ? this.actionTogglingController : undefined;
    }

    public toggleActionView(expand: boolean) {

        const actionsListController = this.getActionTogglingController();

        if (actionsListController) {
            const viewerContextData = this.viewerContextData;

            if (viewerContextData.mode === BymoPageViewerMode.Live) {
                this.clientServices.viewerAnalyticsClient.logEvent('uouToggleAction', {
                    actionId: this.action.id,
                    pageId: viewerContextData.bymoPageId,
                    hypeUserId: viewerContextData.doppeUserId,
                    linkId: viewerContextData.linkId,
                    itemPosition: this.actionIndex,
                    actionType: this.action.actionTypeId,
                    state: expand ? 'expand' : 'collapse',
                    destinationUrl: this.bymoPageContextProps.targetUrl ?? DoppeBiEmptyTargetUrl,
                });

                if (!expand) {
                    this.clientServices.viewerAnalyticsClient.logEvent('uouCloseAction', {
                        actionId: this.action.id,
                        pageId: viewerContextData.bymoPageId,
                        hypeUserId: viewerContextData.doppeUserId,
                        linkId: viewerContextData.linkId,
                        itemPosition: this.actionIndex,
                        actionType: this.action.actionTypeId,
                        destinationUrl: this.bymoPageContextProps.targetUrl ?? DoppeBiEmptyTargetUrl,
                    });
                }
            }

            const widgetOpeningMode = this.action.widgetOpeningMode;
            const normalizedWidgetOpeningMode = evaluateFunction(() => {
                switch (widgetOpeningMode) {
                    case DoppeActionWidgetOpeningMode.OpenCTA:
                    case DoppeActionWidgetOpeningMode.Inline:
                        return widgetOpeningMode;
                    case DoppeActionWidgetOpeningMode.Popup: {
                        return this.canToggleFullScreenMode() ? widgetOpeningMode : DoppeActionWidgetOpeningMode.Inline;
                    }
                }
            })

            switch (normalizedWidgetOpeningMode) {
                case DoppeActionWidgetOpeningMode.Inline: {
                    actionsListController.toggleActionView(this.action.id, expand);
                    break;
                }
                case DoppeActionWidgetOpeningMode.Popup: {

                    this.toggleFullScreenMode(true);

                    this.modalsController.showInnerModal({
                        content: (
                            <DoppeViewerActionPopup
                                action={this.action}
                            />
                        )
                    })

                    break;
                }
                case DoppeActionWidgetOpeningMode.OpenCTA: {
                    break;
                }
            }
        }
    }

    public canToggleFullScreenMode() {
        return this.fullScreenModeTogglingController !== undefined;
    }

    public toggleFullScreenMode(isFullScreen: boolean) {
        this.fullScreenModeTogglingController?.toggleFullScreenMode(this.action.id, isFullScreen)
    }

    public async createStripePaymentIntent(options: {
        price: Price,
        innerItemId?: string,
    }) {
        const {
            price,
            innerItemId
        } = options;
        if (this.viewerContextData.mode !== BymoPageViewerMode.Live) {
            throw new Error('Can only create stripe payment intent in live mode');
        }
        const metadata = {
            actionId: this.action.id,
            pageId: this.viewerContextData.bymoPageId,
            linkId: this.viewerContextData.linkId,
            innerItemId: innerItemId,
        };

        const stripePaymentIntent = await this.clientServices.getApiClient().createStripePaymentIntent({
            price: price,
            metadata: metadata,
        });

        return assertDefined(stripePaymentIntent, 'Failed to create stripe payment intent');
    }

    public getBiIdFields() {
        if (this.viewerContextData.mode === BymoPageViewerMode.Live) {
            return {
                actionId: this.action.id,
                actionType: this.action.actionTypeId,
                pageId: this.viewerContextData.bymoPageId,
                pageType: this.viewerContextData.pageType,
                linkId: this.viewerContextData.linkId,
                hypeUserId: this.viewerContextData.doppeUserId,
            };
        } else {
            throw new Error('Can only get bi id fields in live mode');
        }
    }
}

export namespace DoppeViewerActionContext {

    export interface ActionTogglingController {

        toggleActionView(actionId: DoppeDtoAction.Id, isExpanded: boolean): void;
    }

    export interface FullScreenModeTogglingController {
        toggleFullScreenMode(actionId: DoppeDtoAction.Id, isFullScreen: boolean): void;
    }
}